// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// D-CRX-80 — a focused panel for a long form.
//
// WHY (owner 2026-09-07, on the Enrollment tab): "when u open new enrollment it shows a huge
// section and if u did also edit existing enrollment u see huge section at the same screen ...
// hard to the user to focus."
//
// Both were rendered INLINE, above the queue, and nothing stopped both being open at once — so the
// tab could show two full enrollment forms stacked on top of each other with the worklist pushed
// off the bottom of the screen. Neither form told you which record it belonged to once you had
// scrolled into it.
//
// A side panel fixes the three things that were actually wrong:
//   · ONE at a time — it is a single mounted surface, so a second form cannot appear beside the first
//   · the queue does not move — the list stays where the operator left it
//   · the panel carries a persistent header, so the record being edited is named while you scroll
//
// Escape and a backdrop click close it, the body does not scroll behind it, and focus moves into
// the panel on open — the accessibility floor for anything modal, and cheap to get right once here
// rather than never in three places.
import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'

export interface SidePanelProps {
  open: boolean
  title: string
  /** Secondary line under the title — the record this panel is about. */
  subtitle?: string | null
  onClose: () => void
  children: React.ReactNode
  /** Panel width. Enrollment needs the room; a short form should pass something smaller. */
  width?: number
}

// Every open panel's element. A panel can open from inside another (D-CRX-83: Remove inside the New
// enrollment chooser), and Escape must close only the innermost one, not every panel listening. The
// DOM decides "innermost", not mount order: React runs a child's effects before its parent's.
const openPanels = new Set<HTMLElement>()

export default function SidePanel({ open, title, subtitle, onClose, children, width = 940 }: SidePanelProps) {
  const panelRef = useRef<HTMLDivElement>(null)

  // The LATEST onClose, read through a ref so it is not an effect dependency.
  //
  // Owner, 2026-09-07: "reason keeps bluring while typing". Every caller passes an inline arrow
  // (onClose={() => setEditTarget(null)}), so onClose has a NEW identity on every render of the
  // parent. With it in the dependency array, one keystroke anywhere in the tree tore this effect
  // down and re-ran it — and re-running it calls panelRef.focus(), which pulled focus out of
  // whatever the operator was typing in. It cost a character of focus per character typed, in
  // every input behind an open panel, not only the delete reason.
  const onCloseRef = useRef(onClose)
  useEffect(() => { onCloseRef.current = onClose }, [onClose])

  // Escape closes from anywhere in the panel, including inside an input — bound on the document
  // rather than the container so it works before focus has landed. Keyed on `open` ALONE: this is
  // open/close behaviour, and moving focus is something a panel does when it APPEARS, never on a
  // re-render.
  useEffect(() => {
    if (!open) return
    const me = panelRef.current
    if (me) openPanels.add(me)
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (me && [...openPanels].some(el => el !== me && me.contains(el))) return   // a panel inside is open
      onCloseRef.current()
    }
    document.addEventListener('keydown', onKey)
    // The list behind must not scroll under the panel: on a long queue that produced two scrollbars
    // and a form that appeared to move while the operator was reading it.
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    panelRef.current?.focus()
    return () => {
      document.removeEventListener('keydown', onKey); document.body.style.overflow = prev
      if (me) openPanels.delete(me)
    }
  }, [open])

  if (!open) return null

  return (
    <div role="presentation"
         onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}
         style={{ position: 'fixed', inset: 0, background: 'rgba(15,15,15,0.38)', zIndex: 900,
                  display: 'flex', justifyContent: 'flex-end' }}>
      <div ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={title}
           style={{ background: 'var(--color-surface, #fff)', width: '100%', maxWidth: width,
                    height: '100%', display: 'flex', flexDirection: 'column',
                    boxShadow: '-18px 0 50px rgba(0,0,0,0.22)', outline: 'none' }}>
        {/* STICKY header: on a form this long the operator is usually scrolled past the top, and
            "which patient am I editing?" was unanswerable from there. */}
        <div style={{ position: 'sticky', top: 0, zIndex: 2, display: 'flex', alignItems: 'flex-start',
                      gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--color-border, #e5e7eb)',
                      background: 'var(--color-surface, #fff)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{title}</div>
            {subtitle && (
              <div style={{ fontSize: 12.5, color: 'var(--color-text-soft, #6b7280)',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {subtitle}
              </div>
            )}
          </div>
          <button onClick={onClose} aria-label="Close"
                  style={{ border: '1px solid var(--color-border, #e5e7eb)', background: 'transparent',
                           borderRadius: 8, padding: '5px 8px', cursor: 'pointer', lineHeight: 0 }}>
            <X size={16} />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px 28px' }}>{children}</div>
      </div>
    </div>
  )
}
